import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { DemoAccount } from "../constants/feature.constants";

const normalizeText = (value) => String(value ?? "").trim().toLowerCase();

const getFirstItem = (value) => (Array.isArray(value) ? value[0] || {} : {});

const readNumber = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
  }
  return 0;
};

const calculateRemainingDays = (source = {}) => {
  const activeDays = readNumber(source?.activeDays, source?.DemoAccount?.[0]?.activeDays);
  const featuresAccessData = Array.isArray(source?.FeaturesAccessData)
    ? source.FeaturesAccessData
    : Array.isArray(source?.user?.FeaturesAccessData)
      ? source.user.FeaturesAccessData
      : [];

  if (featuresAccessData.length === 0) {
    return activeDays || null;
  }

  const expiredDateValue =
    featuresAccessData[0]?.expiredDate ||
    featuresAccessData[0]?.ExpiredDate ||
    source?.PlanExpriDate ||
    source?.user?.PlanExpriDate;

  if (!expiredDateValue) {
    return activeDays || null;
  }

  const expiredDate = new Date(expiredDateValue);
  if (Number.isNaN(expiredDate.getTime())) {
    return activeDays || null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  expiredDate.setHours(0, 0, 0, 0);

  const timeDiff = expiredDate - today;
  return Math.max(0, Math.ceil(timeDiff / (1000 * 60 * 60 * 24)));
};

const buildLimit = (source, label, icon, accentColor, dailyKeys, totalKeys) => {
  const record = getFirstItem(source);

  return {
    label,
    icon,
    accentColor,
    daily: readNumber(...dailyKeys.map((key) => record?.[key])),
    total: readNumber(...totalKeys.map((key) => record?.[key])),
  };
};

export const buildContactCreditsPopupData = (source = {}, options = {}) => {
  const planName =
    source?.plan ||
    source?.PlanName ||
    source?.planName ||
    source?.user?.plan ||
    source?.user?.planName ||
    source?.DemoAccount?.[0]?.PlanName ||
    source?.DemoAccount?.[0]?.planName ||
    "";

  const isDemoAccount = normalizeText(planName) === normalizeText(DemoAccount.DEMO_ACCOUNT);
  const threshold = isDemoAccount ? Number(options.demoThreshold ?? 10) : Number(options.standardThreshold ?? 100);
  const daysRemaining = calculateRemainingDays(source);

  const limits = [
    buildLimit(
      source?.OwnerContact || source?.user?.OwnerContact,
      "Owner Contacts",
      "🏠",
      "#FF6B6B",
      ["OwnerDailyContactLimit", "ownerDailyContactLimit"],
      ["OwnerTotalContactLimit", "ownerTotalContactLimit"]
    ),
    buildLimit(
      source?.BrokerContact || source?.user?.BrokerContact,
      "Broker Contacts",
      "🏢",
      "#4ECDC4",
      ["brokerContactLimit", "BrokerDailyContactLimit"],
      ["BrokerTotalContactLimit", "brokerTotalContactLimit"]
    ),
    buildLimit(
      source?.BuilderContact || source?.user?.BuilderContact,
      "Builder Contacts",
      "🏗️",
      "#FFD93D",
      ["builderContactLimit", "BuilderDailyContactLimit"],
      ["BuilderTotalContactLimit", "builderTotalContactLimit"]
    ),
    buildLimit(
      source?.RequirementContact || source?.user?.RequirementContact,
      "Requirement Contacts",
      "📋",
      "#9B59B6",
      ["requirementContactLimit", "RequirementDailyContactLimit"],
      ["RequirementTotalContactLimit", "requirementTotalContactLimit"]
    ),
  ];

  const activeLimits = limits.filter((limit) => limit.total > 0 && limit.total < threshold);

  if (activeLimits.length === 0) {
    return null;
  }

  return {
    planName: planName || (isDemoAccount ? DemoAccount.DEMO_ACCOUNT : "Current Plan"),
    isDemoAccount,
    threshold,
    daysRemaining,
    title: isDemoAccount ? "Demo Contact Credits Running Low" : "Contact Credits Running Low",
    badgeLabel: isDemoAccount ? "Demo Account" : "Active Plan",
    message: isDemoAccount
      ? "Your demo plan is active, but your contact credits are getting close to the limit."
      : `Your ${planName || "current"} plan is below the recommended contact credit threshold.`,
    ctaLabel: isDemoAccount ? "Purchase Plans" : "Upgrade Plan",
    limits: activeLimits,
  };
};

const ContactCreditsPopup = ({ isOpen, onClose, popupData }) => {
  const navigate = useNavigate();
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    }
  }, [isOpen]);

  if (!isOpen || !popupData) return null;

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(onClose, 300);
  };

  const handleAction = () => {
    navigate("/subscription");
    handleClose();
  };

  const headerGradient = popupData.isDemoAccount
    ? "from-[#503691] to-[#3f2d7a]"
    : "from-[#0f766e] to-[#115e59]";

  const limitLabel = popupData.threshold === 1 ? "credit" : "credits";

  return (
    <>
      <style>{`
        @keyframes popupCreditsSlideIn {
          0% {
            opacity: 0;
            transform: scale(0.9) translateY(32px);
          }
          100% {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        @keyframes popupCreditsFloat {
          0% {
            transform: translate(0, 0);
          }
          100% {
            transform: translate(40px, 40px);
          }
        }

        @keyframes popupCreditsPulse {
          0%, 100% {
            box-shadow: 0 0 0 0 rgba(80, 54, 145, 0.35);
          }
          50% {
            box-shadow: 0 0 12px 4px rgba(80, 54, 145, 0.18);
          }
        }

        .contact-popup-card {
          animation: popupCreditsSlideIn 0.45s ease-out;
        }

        .contact-popup-pattern::before {
          content: '';
          position: absolute;
          inset: -50% -50% auto auto;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 1px, transparent 1px);
          background-size: 40px 40px;
          animation: popupCreditsFloat 20s linear infinite;
        }

        .contact-popup-pulse {
          animation: popupCreditsPulse 2s ease-in-out infinite;
        }
      `}</style>

      <div
        className={`fixed inset-0 z-[1000] flex items-center justify-center p-5 bg-[rgba(20,29,50,0.7)] backdrop-blur-sm transition-opacity duration-300 ${
          isAnimating ? "opacity-100" : "opacity-0"
        }`}
        onClick={handleClose}
      >
        <div
          className={`contact-popup-card relative w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-[20px] bg-white shadow-[0_20px_60px_rgba(80,54,145,0.2)] transition-all duration-[400ms] ${
            isAnimating ? "scale-100 translate-y-0" : "scale-90 translate-y-[30px]"
          }`}
          style={{ transitionTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
          onClick={(event) => event.stopPropagation()}
        >
          <button
            className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border-0 bg-[rgba(255,255,255,0.16)] text-white transition-all duration-300 hover:rotate-90 hover:bg-[rgba(255,255,255,0.24)]"
            onClick={handleClose}
          >
            <X size={22} />
          </button>

          <div className={`contact-popup-pattern relative overflow-hidden rounded-t-[20px] bg-gradient-to-br ${headerGradient} px-6 pb-5 pt-5 text-center text-white`}>
            <div className="mb-4 inline-flex items-center gap-2 rounded-[30px] bg-[rgba(255,255,255,0.2)] px-4 py-1">
              <span className="text-lg">{popupData.isDemoAccount ? "🎉" : "⚡"}</span>
              <span className="text-[12px] font-bold uppercase tracking-wider">{popupData.badgeLabel}</span>
            </div>
            <h2 className="my-3 text-[18px] font-bold text-white">{popupData.title}</h2>
            <p className="m-0 text-sm text-[rgba(255,255,255,0.92)]">{popupData.message}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[12px] font-semibold text-white/90">
              <span className="rounded-full bg-[rgba(255,255,255,0.14)] px-3 py-1">
                {popupData.planName}
              </span>
              {popupData.daysRemaining !== null && popupData.daysRemaining !== undefined && (
                <span className="rounded-full bg-[rgba(255,255,255,0.14)] px-3 py-1">
                  {popupData.daysRemaining} day{popupData.daysRemaining === 1 ? "" : "s"} remaining
                </span>
              )}
            </div>
          </div>

          <div className="border-b border-[rgba(80,54,145,0.1)] bg-[rgba(80,54,145,0.04)] p-6">
            <div className="mb-3 flex items-center justify-between text-sm font-semibold text-[#141d32]">
              <span>Contact credits left</span>
              
            </div>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3">
              {popupData.limits.map((limit, index) => (
                <div
                  key={`${limit.label}-${index}`}
                  className="flex items-center gap-3 overflow-hidden rounded-xl border bg-gradient-to-br p-3.5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_6px_12px_rgba(80,54,145,0.1)]"
                  style={{
                    borderColor: `${limit.accentColor}33`,
                    backgroundImage: `linear-gradient(135deg, ${limit.accentColor}14, ${limit.accentColor}08)`,
                  }}
                >
                  <div className="min-w-[30px] text-center text-2xl">{limit.icon}</div>
                  <div className="min-w-0 flex-1">
                    <p className="m-0 text-[11px] font-semibold uppercase leading-[1.2] text-[#503691]">{limit.label}</p>
                    <p className="mt-1 mb-0 text-[13px] font-bold text-[#141d32]">
                      <span style={{ color: limit.accentColor }}>{limit.total}</span>
                      <span className="mx-[3px] text-[rgba(0,0,0,0.3)]">left</span>
                      {limit.daily > 0 && (
                        <span className="text-xs font-semibold text-[#888]">daily {limit.daily}</span>
                      )}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-3 border-t border-[rgba(80,54,145,0.1)] bg-[rgba(80,54,145,0.04)] p-6 max-[600px]:flex-col max-[600px]:p-4">
            <button
              className="flex-1 rounded-[10px] border-2 border-[#503691] bg-white px-4 py-3 text-center text-sm font-semibold whitespace-nowrap text-[#503691] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[rgba(80,54,145,0.05)] hover:shadow-[0_4px_12px_rgba(80,54,145,0.15)] max-[600px]:px-3 max-[600px]:py-3.5 max-[600px]:text-[13px]"
              onClick={handleClose}
            >
              Continue
            </button>
            <button
              className={`flex-1 rounded-[10px] border-0 bg-gradient-to-br px-4 py-3 text-center text-sm font-semibold whitespace-nowrap text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_20px_rgba(80,54,145,0.3)] active:translate-y-0 max-[600px]:px-3 max-[600px]:py-3.5 max-[600px]:text-[13px] ${
                popupData.isDemoAccount ? "from-[#503691] to-[#7c5cdb]" : "from-[#0f766e] to-[#115e59]"
              }`}
              onClick={handleAction}
            >
              {popupData.ctaLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactCreditsPopup;